import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { APP_CONSTANTS } from '@/constants';
import { StorageUtil } from '@/utils/storage/storageUtil';
import { useBranding } from '@/contexts/BrandingContext';
import { applyBrandCssVariablesToRoot, applyFaviconToDocument } from '@/utils/ConfigOrg/brandColorUtils';

export type ThemePreference = 'light' | 'dark' | 'system' | 'org';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextState {
  preference: ThemePreference;
  theme: ResolvedTheme;
  setPreference: (pref: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextState>({} as ThemeContextState);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { branding } = useBranding();

  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    return StorageUtil.get<ThemePreference>(APP_CONSTANTS.THEME_KEY) || 'org';
  });

  const [theme, setTheme] = useState<ResolvedTheme>('light');

  const setPreference = (pref: ThemePreference) => {
    setPreferenceState(pref);
    StorageUtil.set(APP_CONSTANTS.THEME_KEY, pref);
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const resolveAndApplyTheme = () => {
      let resolved: ResolvedTheme = 'light';

      if (preference === 'org') {
        resolved = branding.themeMode === 'dark' ? 'dark' : 'light';
      } else if (preference === 'system') {
        resolved = mediaQuery.matches ? 'dark' : 'light';
      } else {
        resolved = preference === 'dark' ? 'dark' : 'light';
      }

      setTheme(resolved);

      // Cache the resolved theme so the index.html head script can read it instantly on next load
      localStorage.setItem('qhrms_cached_theme', resolved);

      // Sync dark mode class & color scheme on html root
      document.documentElement.classList.toggle('dark', resolved === 'dark');
      document.documentElement.style.colorScheme = resolved;

      // Apply the computed brand CSS variables for this active theme
      applyBrandCssVariablesToRoot(
        branding.brandColor,
        branding.fontColor,
        resolved,
        branding.bodyFontColor,
      );

      // Apply favicon and meta theme color
      applyFaviconToDocument(branding.favicon);
      const themeMeta = document.querySelector<HTMLMetaElement>("meta[name='theme-color']");
      if (themeMeta) {
        themeMeta.content = branding.brandColor;
      }
    };

    resolveAndApplyTheme();

    if (preference === 'system') {
      mediaQuery.addEventListener('change', resolveAndApplyTheme);
      return () => mediaQuery.removeEventListener('change', resolveAndApplyTheme);
    }
  }, [preference, branding]);

  return (
    <ThemeContext.Provider value={{ preference, theme, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
